import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  ACTIVITY_WRITE_THROTTLE_MS,
  AUTH_BROADCAST_CHANNEL_NAME,
  AUTH_LAST_ACTIVITY_KEY,
  AUTH_LOGOUT_BROADCAST_KEY,
  AUTH_REDIRECT_MESSAGE_KEY,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_THROTTLE_MS,
  IDLE_TIMEOUT_MS,
  SESSION_INVALID_MESSAGE,
  SESSION_TIMEOUT_MESSAGE,
  TOKEN_STORAGE_KEY,
  getStoredTimestamp,
} from "@/lib/authSession";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeSessionMessage = (message) => {
  const normalizedMessage = String(message || "").trim();
  if (!normalizedMessage) {
    return SESSION_INVALID_MESSAGE;
  }
  if (normalizedMessage.toLowerCase().includes("tidak ada aktivitas")) {
    return SESSION_TIMEOUT_MESSAGE;
  }
  return normalizedMessage;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityWriteRef = useRef(0);
  const lastHeartbeatRef = useRef(0);
  const broadcastChannelRef = useRef(null);

  const setAxiosToken = useCallback((token) => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      return;
    }
    delete axios.defaults.headers.common.Authorization;
  }, []);

  const broadcastLogout = useCallback((reason) => {
    const payload = JSON.stringify({
      at: Date.now(),
      reason: reason ? normalizeSessionMessage(reason) : "",
    });
    localStorage.setItem(AUTH_LOGOUT_BROADCAST_KEY, payload);
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: "logout",
        reason: reason ? normalizeSessionMessage(reason) : "",
        at: Date.now(),
      });
    }
  }, []);

  const setSharedLastActivity = useCallback((timestamp = Date.now(), options = {}) => {
    const { force = false, broadcast = true } = options;
    if (!force && timestamp - lastActivityWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }

    lastActivityWriteRef.current = timestamp;
    localStorage.setItem(AUTH_LAST_ACTIVITY_KEY, String(timestamp));

    if (broadcast && broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: "activity",
        at: timestamp,
      });
    }
  }, []);

  const clearClientAuth = useCallback((reason = "", options = {}) => {
    const { broadcast = true } = options;

    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
    setAxiosToken(null);
    setUser(null);
    setLoading(false);
    lastActivityWriteRef.current = 0;
    lastHeartbeatRef.current = 0;

    if (reason) {
      sessionStorage.setItem(AUTH_REDIRECT_MESSAGE_KEY, normalizeSessionMessage(reason));
    }

    if (broadcast) {
      broadcastLogout(reason);
    }
  }, [broadcastLogout, setAxiosToken]);

  const revokeSessionOnServer = useCallback((token) => {
    if (!token) {
      return;
    }

    void axios.post(
      `${API}/auth/logout`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        skipAuthSessionHandler: true,
      }
    ).catch(() => {});
  }, []);

  const logout = useCallback((options = {}) => {
    const {
      reason = "",
      revokeServer = true,
      broadcast = true,
    } = options;
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (revokeServer && token) {
      revokeSessionOnServer(token);
    }

    clearClientAuth(reason, { broadcast });
  }, [clearClientAuth, revokeSessionOnServer]);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setLoading(false);
      return null;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        skipAuthSessionHandler: true,
      });
      setUser(response.data);

      const now = Date.now();
      const storedLastActivity = getStoredTimestamp(AUTH_LAST_ACTIVITY_KEY);
      if (storedLastActivity > 0) {
        lastActivityWriteRef.current = storedLastActivity;
      } else {
        setSharedLastActivity(now, { force: true });
      }
      lastHeartbeatRef.current = now;
      return response.data;
    } catch (error) {
      clearClientAuth(error.response?.data?.detail || SESSION_INVALID_MESSAGE, {
        broadcast: true,
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearClientAuth, setSharedLastActivity]);

  const login = async (username, password) => {
    const normalizedUsername = String(username || "").trim();
    const response = await axios.post(`${API}/auth/login`, {
      username: normalizedUsername,
      password,
    });
    const { access_token, user: userData } = response.data;

    localStorage.setItem(TOKEN_STORAGE_KEY, access_token);
    sessionStorage.removeItem(AUTH_REDIRECT_MESSAGE_KEY);
    setAxiosToken(access_token);
    setUser(userData);
    setLoading(false);

    const now = Date.now();
    setSharedLastActivity(now, { force: true });
    lastHeartbeatRef.current = now;
    return userData;
  };

  const signupAdmin = async ({ username, email, password }) => {
    const response = await axios.post(`${API}/auth/signup`, {
      username,
      email,
      password,
    });
    return response.data;
  };

  const updateProfile = async ({ email, password }) => {
    const payload = {};
    if (typeof email === "string") {
      payload.email = email;
    }
    if (password) {
      payload.password = password;
    }
    const response = await axios.put(`${API}/auth/profile`, payload);
    setUser(response.data);
    return response.data;
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      setAxiosToken(token);
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser, setAxiosToken]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }

    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL_NAME);
    broadcastChannelRef.current = channel;

    channel.onmessage = (event) => {
      const data = event.data || {};
      if (data.type === "logout") {
        clearClientAuth(data.reason || "", { broadcast: false });
        return;
      }

      if (data.type === "activity" && typeof data.at === "number") {
        lastActivityWriteRef.current = data.at;
      }
    };

    return () => {
      if (broadcastChannelRef.current === channel) {
        broadcastChannelRef.current = null;
      }
      channel.close();
    };
  }, [clearClientAuth]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === AUTH_LOGOUT_BROADCAST_KEY && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue);
          clearClientAuth(payload.reason || "", { broadcast: false });
        } catch {
          clearClientAuth("", { broadcast: false });
        }
        return;
      }

      if (event.key === AUTH_LAST_ACTIVITY_KEY && event.newValue) {
        const nextTimestamp = Number(event.newValue);
        if (Number.isFinite(nextTimestamp) && nextTimestamp > 0) {
          lastActivityWriteRef.current = nextTimestamp;
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clearClientAuth]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const requestUrl = String(error.config?.url || "");
        const hasToken = Boolean(localStorage.getItem(TOKEN_STORAGE_KEY));
        const shouldIgnore =
          error.config?.skipAuthSessionHandler ||
          !hasToken ||
          status !== 401 ||
          requestUrl.includes("/auth/login") ||
          requestUrl.includes("/auth/signup");

        if (!shouldIgnore) {
          clearClientAuth(error.response?.data?.detail || SESSION_INVALID_MESSAGE, {
            broadcast: true,
          });
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, [clearClientAuth]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const recordActivity = () => {
      setSharedLastActivity(Date.now());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setSharedLastActivity(Date.now(), { force: true });
      }
    };

    const handleFocus = () => {
      setSharedLastActivity(Date.now(), { force: true });
    };

    const intervalId = window.setInterval(() => {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) {
        return;
      }

      const now = Date.now();
      const lastActivityAt = getStoredTimestamp(AUTH_LAST_ACTIVITY_KEY);
      if (lastActivityAt > 0 && now - lastActivityAt > IDLE_TIMEOUT_MS) {
        logout({
          reason: SESSION_TIMEOUT_MESSAGE,
          revokeServer: true,
          broadcast: true,
        });
        return;
      }

      if (document.visibilityState !== "visible") {
        return;
      }
      if (lastActivityAt <= 0 || now - lastActivityAt > HEARTBEAT_THROTTLE_MS) {
        return;
      }
      if (now - lastHeartbeatRef.current < HEARTBEAT_THROTTLE_MS) {
        return;
      }

      lastHeartbeatRef.current = now;
      void axios.post(`${API}/auth/activity`, {}, { skipAuthSessionHandler: true }).catch((error) => {
        if (error.response?.status === 401) {
          clearClientAuth(error.response?.data?.detail || SESSION_INVALID_MESSAGE, {
            broadcast: true,
          });
          return;
        }
        lastHeartbeatRef.current = 0;
      });
    }, HEARTBEAT_INTERVAL_MS);

    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [clearClientAuth, logout, user, setSharedLastActivity]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, signupAdmin, updateProfile, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
